import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Spin,
  message,
  Button,
  Space,
  Modal,
  Form,
  Input
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { 
  fetchProjectBids, 
  createBid, 
  updateBid, 
  deleteBid,
  resendUpdatedBidEmail
} from "../../features/Projects/projectSlice";

const BidsManagementPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { bids, bidsLoading } = useSelector((state) => state.projects);

  const [modalVisible, setModalVisible] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [form] = Form.useForm();
  const [resendLoading, setResendLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectBids(projectId))
        .unwrap()
        .catch(() => message.error("Failed to fetch bids."));
    }
  }, [dispatch, projectId]);

  const columns = [
    { title: "Translator", dataIndex: ["translator", "name"], key: "translator" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color =
          status === "submitted"
            ? "green"
            : status === "pending"
            ? "orange"
            : "red";
        return <Tag color={color}>{status}</Tag>;
      }
    },
    { title: "Submitted At", dataIndex: "created_at", key: "created_at" },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEditModal(record)}>
            Edit
          </Button>
          <Button type="link" onClick={() => handleResendEmail(record)}>
            {resendLoading ? <Spin size="small" /> : "Resend Email"}
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      )
    }
  ];

  // Expanded row: shows detailed bid info.
  const expandedRowRender = (record) => (
    <div>
      <p><strong>Translator Details:</strong></p>
      {/* <p>ID: {record.translator.id}</p> */}
      <p>Name: {record.translator.name}</p>
      <p><strong>Bid Services:</strong></p>
      {record.services && record.services.length > 0 ? (
        <Table
          columns={[
            { title: "Service Name", dataIndex: "service_name", key: "service_name" },
            { title: "Total Words", dataIndex: "total_words", key: "total_words" },
            { title: "Unit Cost", dataIndex: "unit_cost", key: "unit_cost" },
            { title: "Total Hours", dataIndex: "total_hours", key: "total_hours" },
            { title: "Per Hour", dataIndex: "per_hour", key: "per_hour" },
            { title: "Total Pages", dataIndex: "total_pages", key: "total_pages" },
            { title: "Per Page", dataIndex: "per_page", key: "per_page" },
            { title: "Total Quote", dataIndex: "total_quote", key: "total_quote" },
            { title: "Reject Reason", dataIndex: "reject_reason", key: "reject_reason" },
            { title: "Target Language", dataIndex: "target_language", key: "target_language" }
          ]}
          dataSource={record.services}
          pagination={false}
          rowKey={(row) => row.service_id + "_" + row.target_language_id}
          size="small"
        />
      ) : (
        <p>No service details available.</p>
      )}
    </div>
  );

  const openEditModal = (bid) => {
    setEditingBid(bid);
    // Set initial values: translator_id, and for each service, include IDs and read-only friendly names.
    form.setFieldsValue({
      translator_id: bid.translator.id,
      services: bid.services.map((s) => ({
        service_id: s.service_id,
        target_language_id: s.target_language_id,
        service_name: s.service_name,         // For display only.
        target_language: s.target_language,     // For display only.
        total_words: s.total_words,
        unit_cost: s.unit_cost,
        total_hours: s.total_hours,
        per_hour: s.per_hour,
        total_pages: s.total_pages,
        per_page: s.per_page,
        total_quote: s.total_quote,
        reject_reason: s.reject_reason
      }))
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingBid(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleModalOk = () => {
    setSubmitLoading(true);
    form.validateFields()
      .then((values) => {
        const formData = values;
        if (editingBid) {
          dispatch(updateBid({ projectId, bidId: editingBid.id, formData }))
            .unwrap()
            .then(() => {
              message.success("Bid updated successfully");
              setModalVisible(false);
              setSubmitLoading(false);
            })
            .catch(() => {
              message.error("Failed to update bid");
              setSubmitLoading(false);
            });
        } else {
          dispatch(createBid({ projectId, formData }))
            .unwrap()
            .then(() => {
              message.success("Bid created successfully");
              setModalVisible(false);
              setSubmitLoading(false);
            })
            .catch(() => {
              message.error("Failed to create bid");
              setSubmitLoading(false);
            });
        }
      })
      .catch((info) => {
        console.log("Validation Failed:", info);
        setSubmitLoading(false);
      });
  };

  const handleResendEmail = (bid) => {
    setResendLoading(true);
    dispatch(resendUpdatedBidEmail({ projectId, bidId: bid.id }))
      .unwrap()
      .then(() => {
        message.success("Updated bid email sent successfully");
        setResendLoading(false);
      })
      .catch((error) => {
        message.error(error.message || "Failed to send updated email");
        setResendLoading(false);
      });
  };

  const handleDelete = (bidId) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: "Are you sure you want to delete this bid?",
      onOk: () => {
        dispatch(deleteBid({ projectId, bidId }))
          .unwrap()
          .then(() => message.success("Bid deleted successfully"))
          .catch(() => message.error("Failed to delete bid"));
      }
    });
  };

  const projectBids = bids[projectId] || [];

  return (
    <div style={{ padding: "20px" }}>
      <Space style={{ marginBottom: "16px" }}>
        <Button onClick={() => navigate(-1)}>Back to Projects</Button>
        {/* Uncomment below if you want to create new bids */}
        {/* <Button type="primary" onClick={openCreateModal}>Create Bid</Button> */}
      </Space>
      <h2>Bids for Project ID: {projectId}</h2>
      {bidsLoading ? (
        <Spin />
      ) : (
        <Table
          dataSource={projectBids}
          columns={columns}
          expandable={{ expandedRowRender }}
          rowKey="id"
        />
      )}

      <Modal
        title={editingBid ? "Edit Bid" : "Create Bid"}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
        okButtonProps={{ loading: submitLoading }}
        width={800}
      >
        <Form form={form} layout="vertical">
          {/* Display translator name as read-only */}
          <Form.Item label="Translator">
            <Input value={editingBid ? editingBid.translator.name : ""} disabled />
            <Form.Item name="translator_id" noStyle>
              <Input type="hidden" />
            </Form.Item>
          </Form.Item>

          <Form.List name="services">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => {
                  const serviceName = form.getFieldValue(["services", index, "service_name"]) || "";
                  const targetLanguage = form.getFieldValue(["services", index, "target_language"]) || "";
                  return (
                    <div
                      key={field.key}
                      style={{
                        border: "1px solid #f0f0f0",
                        padding: "16px",
                        marginBottom: "16px"
                      }}
                    >
                      {/* Display read-only service name and target language */}
                      <Form.Item label="Service">
                        <Input value={serviceName} disabled />
                      </Form.Item>
                      <Form.Item label="Target Language">
                        <Input value={targetLanguage} disabled />
                      </Form.Item>
                      {/* Hidden fields for IDs */}
                      <Form.Item
                        {...field}
                        name={[field.name, "service_id"]}
                        fieldKey={[field.fieldKey, "service_id"]}
                        style={{ display: "none" }}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "target_language_id"]}
                        fieldKey={[field.fieldKey, "target_language_id"]}
                        style={{ display: "none" }}
                      >
                        <Input />
                      </Form.Item>
                      {/* Editable cost fields */}
                      <Form.Item {...field} name={[field.name, "total_words"]} label="Total Words">
                        <Input type="number" />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, "unit_cost"]} label="Unit Cost">
                        <Input type="number" step="0.01" />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, "total_hours"]} label="Total Hours">
                        <Input type="number" />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, "per_hour"]} label="Per Hour">
                        <Input type="number" step="0.01" />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, "total_pages"]} label="Total Pages">
                        <Input type="number" />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, "per_page"]} label="Per Page">
                        <Input type="number" step="0.01" />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, "total_quote"]} label="Total Quote">
                        <Input type="number" step="0.01" />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, "reject_reason"]} label="Reject Reason">
                        <Input />
                      </Form.Item>
                      {/* In edit mode, we hide the remove button and disable adding new services */}
                      {editingBid ? null : (
                        <Button type="link" onClick={() => remove(field.name)}>
                          Remove Service
                        </Button>
                      )}
                    </div>
                  );
                })}
                {editingBid ? null : (
                  <Button type="dashed" onClick={() => add()} block>
                    + Add Service
                  </Button>
                )}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default BidsManagementPage;
